const RESTORABLE_ASSIGNMENT_FIELDS = [
	"employee_id",
	"group_id",
	"day",
	"shift_id",
	"function_id",
	"comments",
	"locked",
	"ubication_ids",
	"time_intervals",
];

function mapTimeIntervals(intervals) {
	if (!intervals?.length) {
		return undefined;
	}

	return intervals.map((interval) => ({
		start: interval.start,
		end: interval.end,
		time_type_id: interval.time_type_id ?? interval.time_type?.id,
	}));
}

function hasCustomTimeIntervals(shiftPlan) {
	// Manual sidebar assignments keep template intervals on the object but
	// time_intervals_from_manual stays false. Only send intervals when the
	// user (or hourly resize) actually edited them — same rule as ShiftSelector.
	return (
		!!shiftPlan?.time_intervals_from_manual &&
		Array.isArray(shiftPlan?.time_intervals) &&
		shiftPlan.time_intervals.length > 0
	);
}

/**
 * Body for plansService.add when restoring a deleted assignment.
 * Mirrors ShiftSelector / move payloads — avoids sending nested GET blobs.
 */
export function buildPlanRestoreBody(shiftPlan, employeeId, day) {
	if (!shiftPlan || !employeeId) {
		return null;
	}

	const shiftId = shiftPlan.shift?.id ?? shiftPlan.shift_id;
	const restoreDay = day ?? shiftPlan.day ?? shiftPlan.span_origin_day;

	if (!shiftId || !restoreDay) {
		return null;
	}

	const body = {
		day: restoreDay,
		employee_id: employeeId,
		shift_id: shiftId,
		// Desired lock state. POST /plans/ requires locked:true; the executor
		// creates locked and PATCHes unlocked when this is false.
		locked: shiftPlan.locked ?? true,
		ubication_ids: [],
	};

	const functionId = shiftPlan.function?.id ?? shiftPlan.function_id;
	if (functionId) {
		body.function_id = functionId;
	}

	if (shiftPlan.comments) {
		body.comments = shiftPlan.comments;
	}

	const ubicationIds = (shiftPlan.ubications || [])
		.map((ubication) => ubication.id)
		.filter(Boolean);
	if (ubicationIds.length > 0) {
		body.ubication_ids = ubicationIds;
	}

	if (hasCustomTimeIntervals(shiftPlan)) {
		body.time_intervals = mapTimeIntervals(shiftPlan.time_intervals);
	}

	return body;
}

export function pickRestorableFields(assignment) {
	if (!assignment || typeof assignment !== "object") {
		return {};
	}

	return RESTORABLE_ASSIGNMENT_FIELDS.reduce((accumulator, field) => {
		if (assignment[field] !== undefined) {
			accumulator[field] = assignment[field];
		}
		return accumulator;
	}, {});
}

export function diffAssignment(before, after) {
	const delta = {};
	const keys = new Set([
		...Object.keys(before || {}),
		...Object.keys(after || {}),
	]);

	keys.forEach((key) => {
		const beforeValue = before?.[key];
		const afterValue = after?.[key];
		if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
			delta[key] = beforeValue;
		}
	});

	return delta;
}

export function buildCreateInverse(createdId, employeeId) {
	return {
		action: "DELETE",
		requests: [
			{
				service: "plansService",
				method: "remove",
				args: [createdId],
			},
		],
		refresh: { employeeIds: [employeeId] },
	};
}

/**
 * Undo replacing a default rest shift: remove the new assignment and restore
 * the previous default shift snapshot (including locked: false when applicable).
 */
export function buildReplaceDefaultShiftInverse({
	createdId,
	replacedDefaultRestoreBody,
	employeeId,
}) {
	if (!createdId) {
		return null;
	}

	if (!replacedDefaultRestoreBody?.employee_id || !replacedDefaultRestoreBody?.day) {
		return buildCreateInverse(createdId, employeeId);
	}

	const employeeIds = [
		...new Set(
			[employeeId, replacedDefaultRestoreBody.employee_id].filter(Boolean)
		),
	];

	return {
		action: "REPLACE_DEFAULT_SHIFT",
		requests: [
			{
				service: "plansService",
				method: "remove",
				args: [createdId],
			},
			{
				service: "plansService",
				method: "add",
				args: [replacedDefaultRestoreBody],
			},
		],
		refresh: { employeeIds },
	};
}

/**
 * Undo bulk paste/create: remove all assignments created in the forward step.
 */
export function buildBulkRemoveInverse(createdIds, employeeIds = []) {
	const plans = (createdIds || [])
		.filter((id) => id != null && id !== -1)
		.map((id) => ({ id }));

	if (plans.length === 0) {
		return null;
	}

	return {
		action: "BULK_DELETE",
		requests: [
			{
				service: "plansService",
				method: "removeMultiple",
				args: [plans],
			},
		],
		refresh: {
			employeeIds: [...new Set((employeeIds || []).filter(Boolean))],
		},
	};
}

export function buildUpdateInverse({ id, before, after, employeeId }) {
	const body = diffAssignment(before, after);
	delete body.id;

	if (!id || Object.keys(body).length === 0) {
		return null;
	}

	return {
		action: "PATCH",
		requests: [
			{
				service: "plansService",
				method: "updateAssignment",
				args: [{ id, ...body }],
			},
		],
		refresh: { employeeIds: [employeeId] },
	};
}

export function buildBulkUpdateInverse(updates = []) {
	const requests = [];
	const employeeIds = new Set();

	(updates || []).forEach((update) => {
		const inverse = buildUpdateInverse(update);
		if (!inverse) {
			return;
		}

		requests.push(...inverse.requests);
		(inverse.refresh?.employeeIds || []).forEach((id) => employeeIds.add(id));
	});

	if (requests.length === 0) {
		return null;
	}

	return {
		action: "BULK_UPDATE",
		requests,
		refresh: { employeeIds: [...employeeIds] },
	};
}

function buildAssignmentFieldRestoreBody({ id, before, employeeId, fields }) {
	const body = {
		id,
		day: before.day,
		employee_id: before.employee_id ?? employeeId,
		shift_id: before.shift_id,
		locked: before.locked ?? true,
	};

	fields.forEach((field) => {
		if (field === "ubication_ids") {
			body.ubication_ids = before.ubication_ids ?? [];
			return;
		}

		if (before[field] !== undefined) {
			body[field] = before[field];
		}
	});

	return body;
}

/**
 * Undo bulk field assignment (setFunctions / setUbications): restore previous values.
 */
export function buildBulkFieldRestoreInverse(snapshots, fields = [], chunkSize = 50) {
	const bodies = (snapshots || [])
		.map(({ id, before, employeeId }) =>
			buildAssignmentFieldRestoreBody({ id, before, employeeId, fields })
		)
		.filter((body) => body.id != null);

	if (bodies.length === 0) {
		return null;
	}

	const requests = [];
	for (let index = 0; index < bodies.length; index += chunkSize) {
		requests.push({
			service: "plansService",
			method: "updateMultiple",
			args: [bodies.slice(index, index + chunkSize)],
		});
	}

	const employeeIds = [
		...new Set(
			(snapshots || []).map((snapshot) => snapshot.employeeId).filter(Boolean)
		),
	];

	return {
		action: "BULK_UPDATE",
		requests,
		refresh: { employeeIds },
	};
}

export function buildDeleteInverseFromBody(body) {
	if (!body?.employee_id || !body?.day || !body?.shift_id) {
		return null;
	}

	return {
		action: "CREATE",
		requests: [
			{
				service: "plansService",
				method: "add",
				args: [body],
			},
		],
		refresh: { employeeIds: [body.employee_id] },
	};
}

export function buildDeleteInverse(assignment, employeeId, day) {
	const body = buildPlanRestoreBody(
		assignment,
		employeeId ?? assignment?.employee_id ?? assignment?.employee?.id,
		day ?? assignment?.day
	);

	return buildDeleteInverseFromBody(body);
}

/**
 * Undo move: reverse of create-at-destination + delete-at-source.
 * Restores the assignment at the source cell, then removes the copy at destination.
 * If the destination had a default rest, restore it after the copy is removed
 * so auto-recreated rests keep their original lock state.
 */
export function buildMoveInverse({
	createdId,
	sourceRestoreBody,
	sourceEmployeeId,
	destinationEmployeeId,
	replacedDefaultRestoreBodies = [],
}) {
	if (!createdId || !sourceRestoreBody?.employee_id || !sourceRestoreBody?.day) {
		return null;
	}

	const restoreBodies = (replacedDefaultRestoreBodies || []).filter(
		(body) => body?.employee_id && body?.day && body?.shift_id
	);

	const employeeIds = [
		...new Set(
			[
				sourceEmployeeId,
				destinationEmployeeId,
				sourceRestoreBody.employee_id,
				...restoreBodies.map((body) => body.employee_id),
			].filter(Boolean)
		),
	];

	const requests = [
		{
			service: "plansService",
			method: "add",
			args: [sourceRestoreBody],
		},
		{
			service: "plansService",
			method: "remove",
			args: [createdId],
		},
		...restoreBodies.map((body) => ({
			service: "plansService",
			method: "add",
			args: [body],
		})),
	];

	return {
		action: "MOVE",
		requests,
		refresh: { employeeIds },
	};
}

export function buildBulkDeleteInverse(assignments) {
	const bodies = (assignments || [])
		.map((assignment) => {
			const employeeId =
				assignment?.employee_id || assignment?.employee?.id;
			return buildPlanRestoreBody(assignment, employeeId, assignment?.day);
		})
		.filter(Boolean);

	if (bodies.length === 0) {
		return null;
	}

	const employeeIds = [
		...new Set(bodies.map((body) => body.employee_id).filter(Boolean)),
	];

	return {
		action: "BULK_CREATE",
		requests: bodies.map((body) => ({
			service: "plansService",
			method: "add",
			args: [body],
		})),
		refresh: { employeeIds },
	};
}
